import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  TrendingUp,
  Plus,
  Edit,
  Trash2,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  User,
  CheckSquare,
  Eye,
  DollarSign
} from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSync } from "@/contexts/SyncContext";
import { listarContasReceber, listarRecebiveisCompleto, criarContaReceber, atualizarContaReceber, deletarContaReceber, ContaReceber } from "@/integrations/supabase/contas-receber";
import { getCustomers } from "@/integrations/supabase/customers";
import { listarFormasPagamento } from "@/integrations/supabase/formas-pagamento";
import { DialogRegistrarRecebimentosMultiplos } from "@/components/DialogRegistrarRecebimentosMultiplos";
import { listarContasBancarias } from "@/integrations/supabase/contas-bancarias";
import { formatCurrency } from "@/utils/formatCurrency";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ClientSearch } from "@/components/ClientSearch";
import { getTodayAsISO } from "@/utils/dateOnly";

export default function ContasReceber() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { invalidateRelated } = useSync();
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroStatus, setFiltroStatus] = useState<string>("todos");
  const [ordenacao, setOrdenacao] = useState<"recentes" | "antigos" | "cliente-az" | "cliente-za" | "valor-maior" | "valor-menor">("recentes");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingConta, setEditingConta] = useState<ContaReceber | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedClientName, setSelectedClientName] = useState<string>("");
  const [isMarcarRecebidoDialogOpen, setIsMarcarRecebidoDialogOpen] = useState(false);
  const [contaParaReceber, setContaParaReceber] = useState<ContaReceber | null>(null);
  const [dataRecebimento, setDataRecebimento] = useState<string>("");
  const [formaRecebimento, setFormaRecebimento] = useState<string>("");
  const [contaRecebimento, setContaRecebimento] = useState<string>("");
  const [valorRecebidoModal, setValorRecebidoModal] = useState<string>("");
  const [selectedContasIds, setSelectedContasIds] = useState<Set<string>>(new Set());
  const [isMarcarEmLoteDialogOpen, setIsMarcarEmLoteDialogOpen] = useState(false);
  const [dataRecebimentoLote, setDataRecebimentoLote] = useState<string>("");
  const [formaRecebimentoLote, setFormaRecebimentoLote] = useState<string>("");
  const [contaRecebimentoLote, setContaRecebimentoLote] = useState<string>("");
  const [isProcessandoLote, setIsProcessandoLote] = useState(false);
  const [isDialogRecebimentosMultiplosOpen, setIsDialogRecebimentosMultiplosOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState<Partial<ContaReceber & { forma_pagamento_id?: string; conta_bancaria_id?: string }>>({
    descricao: "",
    categoria: "",
    valor_total: 0,
    valor_recebido: 0,
    data_vencimento: "",
    data_recebimento: "",
    status: "pendente",
    forma_pagamento: "",
    forma_pagamento_id: undefined,
    conta_bancaria_id: undefined,
    observacoes: "",
    cliente_id: undefined,
    pedido_id: undefined
  });

  // Buscar TODAS as contas + pedidos em aberto para cálculo de estatísticas
  const { data: todasAsContas = [] } = useQuery({
    queryKey: ["contas_receber_todas"],
    queryFn: () => listarRecebiveisCompleto() // Sem filtro - busca TODOS os recebíveis
  });

  // Buscar contas + pedidos filtrados para a lista
  const { data: contas = [], isLoading } = useQuery({
    queryKey: ["contas_receber", filtroStatus],
    queryFn: () => listarRecebiveisCompleto({ status: filtroStatus !== "todos" ? filtroStatus : undefined })
  });

  const { data: clientes = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: getCustomers
  });

  const { data: formasPagamento = [] } = useQuery({
    queryKey: ["formas_pagamento"],
    queryFn: listarFormasPagamento
  });

  const { data: contasBancarias = [] } = useQuery({
    queryKey: ["contas_bancarias"],
    queryFn: listarContasBancarias
  });

  let contasFiltradas = contas.filter(conta =>
    conta.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
    clientes.find(c => c.id === conta.cliente_id)?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Aplicar ordenação
  contasFiltradas = [...contasFiltradas].sort((a, b) => {
    switch (ordenacao) {
      case "recentes":
        return new Date(b.data_vencimento).getTime() - new Date(a.data_vencimento).getTime();
      case "antigos":
        return new Date(a.data_vencimento).getTime() - new Date(b.data_vencimento).getTime();
      case "cliente-az":
        return (clientes.find(c => c.id === a.cliente_id)?.name || "").localeCompare(
          clientes.find(c => c.id === b.cliente_id)?.name || ""
        );
      case "cliente-za":
        return (clientes.find(c => c.id === b.cliente_id)?.name || "").localeCompare(
          clientes.find(c => c.id === a.cliente_id)?.name || ""
        );
      case "valor-maior":
        return b.valor_total - a.valor_total;
      case "valor-menor":
        return a.valor_total - b.valor_total;
      default:
        return 0;
    }
  });

  const contasRecebidas = todasAsContas.filter(c => c.status === 'recebido' && c.data_recebimento);
  const contasAgrupadasPorData = contasRecebidas.reduce((acc: Record<string, typeof contasRecebidas>, conta) => {
    const data = conta.data_recebimento?.split('T')[0] || 'sem-data';
    if (!acc[data]) {
      acc[data] = [];
    }
    acc[data].push(conta);
    return acc;
  }, {});

  const datasOrdenadas = Object.keys(contasAgrupadasPorData).sort().reverse();

  // Calcular estatísticas usando TODAS as contas (independente do filtro)
  const estatisticas = {
    total: todasAsContas.reduce((acc, conta) => acc + conta.valor_total, 0),
    recebido: todasAsContas.filter(c => c.status === 'recebido').reduce((acc, conta) => acc + conta.valor_total, 0),
    pendente: todasAsContas.filter(c => c.status === 'pendente').reduce((acc, conta) => acc + conta.valor_total, 0),
    atrasado: todasAsContas.filter(c => c.status === 'atrasado').reduce((acc, conta) => acc + conta.valor_total, 0)
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'recebido':
        return <Badge className="bg-green-500"><CheckCircle2 className="h-3 w-3 mr-1" />Recebido</Badge>;
      case 'pendente':
        return <Badge className="bg-yellow-500"><Clock className="h-3 w-3 mr-1" />Pendente</Badge>;
      case 'atrasado':
        return <Badge className="bg-red-500"><XCircle className="h-3 w-3 mr-1" />Atrasado</Badge>;
      case 'cancelado':
        return <Badge variant="outline">Cancelado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleVerPedido = (descricao: string) => {
    if (descricao && descricao.includes('PED-')) {
      const match = descricao.match(/(PED-\d{6}-\d{3})/);
      if (match) {
        navigate(`/pedidos/${match[1]}`);
      }
    }
  };

  const handleOpenDialog = (conta?: ContaReceber) => {
    if (conta) {
      setEditingConta(conta);
      const cliente = clientes.find(c => c.id === conta.cliente_id);
      setSelectedClientName(cliente?.name || "");
      setFormData({
        ...conta,
        data_vencimento: conta.data_vencimento.split('T')[0],
        data_recebimento: conta.data_recebimento?.split('T')[0] || ""
      });
    } else {
      setEditingConta(null);
      setSelectedClientName("");
      setFormData({
        descricao: "",
        categoria: "",
        valor_total: 0,
        valor_recebido: 0,
        data_vencimento: "",
        data_recebimento: "",
        status: "pendente",
        forma_pagamento: "",
        observacoes: "",
        cliente_id: undefined,
        pedido_id: undefined
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingConta(null);
    setSelectedClientName("");
  };

  const handleClientChange = async (clientName: string) => {
    setSelectedClientName(clientName);
    
    if (!clientName) {
      setFormData({ ...formData, cliente_id: undefined });
      return;
    }
    
    // Buscar o ID do cliente pelo nome
    let cliente = clientes.find(c => c.name === clientName);
    
    // Se o cliente não foi encontrado, recarregar lista de clientes
    // Isso pode acontecer quando um novo cliente foi criado
    if (!cliente && clientName) {
      // Aguardar um pouco para garantir que o cliente foi salvo
      await new Promise(resolve => setTimeout(resolve, 300));
      await queryClient.refetchQueries({ queryKey: ["customers"] });
      const updatedClients = await getCustomers();
      cliente = updatedClients.find(c => c.name === clientName);
      
      if (cliente) {
        setFormData({ ...formData, cliente_id: cliente.id });
      } else {
        // Se ainda não encontrou, tentar mais uma vez após mais um delay
        await new Promise(resolve => setTimeout(resolve, 500));
        const finalClients = await getCustomers();
        const finalCliente = finalClients.find(c => c.name === clientName);
        setFormData({ ...formData, cliente_id: finalCliente?.id || undefined });
      }
    } else if (cliente) {
      setFormData({ ...formData, cliente_id: cliente.id });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return; // Prevenir múltiplos cliques

    if (!formData.descricao?.trim()) {
      toast.error("Descrição é obrigatória");
      return;
    }

    if (!formData.valor_total || formData.valor_total <= 0) {
      toast.error("Valor total deve ser maior que zero");
      return;
    }

    if (!formData.data_vencimento || formData.data_vencimento.trim() === "") {
      toast.error("Data de vencimento é obrigatória");
      return;
    }

    setIsSubmitting(true);
    
    // Timeout de segurança para evitar travamento infinito
    const timeoutId = setTimeout(() => {
      setIsSubmitting(false);
      toast.error("Operação está demorando muito. Tente novamente.");
    }, 30000); // 30 segundos

    try {
      // Se um nome de cliente foi selecionado mas não encontramos o ID, buscar novamente
      if (selectedClientName && !formData.cliente_id) {
        // Recarregar clientes e buscar novamente
        await queryClient.refetchQueries({ queryKey: ["customers"] });
        const updatedClients = await getCustomers();
        const clienteAtualizado = updatedClients.find(c => c.name === selectedClientName);
        if (clienteAtualizado) {
          formData.cliente_id = clienteAtualizado.id;
        }
      }

      if (editingConta?.id) {
        const result = await atualizarContaReceber(editingConta.id, formData);
        clearTimeout(timeoutId);
        if (result.ok) {
          toast.success("Conta atualizada com sucesso!");
          invalidateRelated('contas_receber');
          // Invalidar também queries do Fluxo de Caixa
          queryClient.invalidateQueries({ 
            predicate: (query) => {
              const key = query.queryKey[0];
              return key === 'contas_receber' && query.queryKey[1] === 'fluxo';
            }
          });
          handleCloseDialog();
        } else {
          toast.error(result.error || "Erro ao atualizar conta");
        }
      } else {
        const result = await criarContaReceber(formData as Omit<ContaReceber, 'id' | 'created_at' | 'updated_at' | 'empresa_id' | 'valor_recebido'>);
        clearTimeout(timeoutId);
        if (result.ok) {
          toast.success("Conta criada com sucesso!");
          invalidateRelated('contas_receber');
          // Invalidar também queries do Fluxo de Caixa
          queryClient.invalidateQueries({ 
            predicate: (query) => {
              const key = query.queryKey[0];
              return key === 'contas_receber' && query.queryKey[1] === 'fluxo';
            }
          });
          handleCloseDialog();
        } else {
          toast.error(result.error || "Erro ao criar conta");
        }
      }
    } catch (error: any) {
      clearTimeout(timeoutId);
      toast.error(error.message || "Erro ao salvar conta");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir esta conta?")) {
      return;
    }

    try {
      setIsDeleting(id);
      const result = await deletarContaReceber(id);
      if (result.ok) {
        toast.success("Conta excluída com sucesso!");
        invalidateRelated('contas_receber');
        // Invalidar também queries do Fluxo de Caixa
        queryClient.invalidateQueries({ 
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === 'contas_receber' && query.queryKey[1] === 'fluxo';
          }
        });
      } else {
        toast.error(result.error || "Erro ao excluir conta");
      }
    } catch (error: any) {
      toast.error(error.message || "Erro ao excluir conta");
    } finally {
      setIsDeleting(null);
    }
  };

  const handleAbrirMarcarRecebido = (conta: ContaReceber) => {
    setContaParaReceber(conta);
    setDataRecebimento(getTodayAsISO());
    setFormaRecebimento("");
    setContaRecebimento("");
    setValorRecebidoModal(conta.valor_total.toString());
    setIsMarcarRecebidoDialogOpen(true);
  };

  const handleConfirmarMarcarRecebido = async () => {
    if (!contaParaReceber) return;
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const result = await atualizarContaReceber(contaParaReceber.id!, {
        valor_recebido: parseFloat(valorRecebidoModal) || contaParaReceber.valor_total,
        status: 'recebido',
        data_recebimento: dataRecebimento,
        forma_pagamento_id: formaRecebimento || undefined,
        conta_bancaria_id: contaRecebimento || undefined
      });

      if (result.ok) {
        toast.success("Conta marcada como recebida!");
        invalidateRelated('contas_receber');
        // Invalidar também queries do Fluxo de Caixa
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return key === 'contas_receber' && query.queryKey[1] === 'fluxo';
          }
        });
        setIsMarcarRecebidoDialogOpen(false);
        setContaParaReceber(null);
      } else {
        toast.error(result.error || "Erro ao atualizar conta");
      }
    } catch (error: any) {
      toast.error(error.message || "Erro ao marcar como recebido");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSelecionarConta = (contaId: string) => {
    const newSelected = new Set(selectedContasIds);
    if (newSelected.has(contaId)) {
      newSelected.delete(contaId);
    } else {
      newSelected.add(contaId);
    }
    setSelectedContasIds(newSelected);
  };

  const toggleSelecionarTodas = () => {
    if (selectedContasIds.size === contasFiltradas.length) {
      setSelectedContasIds(new Set());
    } else {
      const allIds = new Set(contasFiltradas.map(c => c.id!).filter(id => {
        const conta = contasFiltradas.find(c => c.id === id);
        return conta && conta.status !== 'recebido';
      }));
      setSelectedContasIds(allIds);
    }
  };

  const handleAbrirMarcarEmLote = () => {
    if (selectedContasIds.size === 0) {
      toast.error("Selecione pelo menos uma conta");
      return;
    }
    setDataRecebimentoLote(new Date().toISOString().split('T')[0]);
    setFormaRecebimentoLote("");
    setContaRecebimentoLote("");
    setIsMarcarEmLoteDialogOpen(true);
  };

  const handleConfirmarMarcarEmLote = async () => {
    if (selectedContasIds.size === 0) return;
    if (isProcessandoLote) return;

    setIsProcessandoLote(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      for (const contaId of selectedContasIds) {
        const conta = contasFiltradas.find(c => c.id === contaId);
        if (!conta) continue;

        const result = await atualizarContaReceber(contaId, {
          valor_recebido: conta.valor_total,
          status: 'recebido',
          data_recebimento: dataRecebimentoLote,
          forma_pagamento_id: formaRecebimentoLote || undefined,
          conta_bancaria_id: contaRecebimentoLote || undefined
        });

        if (result.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      }

      toast.success(`✅ ${successCount} conta(s) marcada(s). ${errorCount > 0 ? `❌ ${errorCount} erro(s)` : ''}`);
      invalidateRelated('contas_receber');
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === 'contas_receber' && query.queryKey[1] === 'fluxo';
        }
      });
      setIsMarcarEmLoteDialogOpen(false);
      setSelectedContasIds(new Set());
    } catch (error: any) {
      toast.error(error.message || "Erro ao marcar contas em lote");
    } finally {
      setIsProcessandoLote(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100">
      <PageHeader
        title="Contas a Receber"
        icon={<DollarSign className="w-6 h-6 text-purple-600" />}
        statusIndicatorsAbove
      />

      <div className="container mx-auto p-6 space-y-6">
        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCurrency({ value: estatisticas.total, currency: 'BRL' })}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Recebido</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{formatCurrency({ value: estatisticas.recebido, currency: 'BRL' })}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Pendente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{formatCurrency({ value: estatisticas.pendente, currency: 'BRL' })}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Atrasado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{formatCurrency({ value: estatisticas.atrasado, currency: 'BRL' })}</div>
            </CardContent>
          </Card>
        </div>

        {/* Relatório por Data (Consolidado) */}
        {datasOrdenadas.length > 0 && (
          <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                📊 Recebimentos por Data
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {datasOrdenadas.map((data) => {
                  const contas = contasAgrupadasPorData[data];
                  const totalData = contas.reduce((sum, c) => sum + c.valor_recebido, 0);
                  const countData = contas.length;
                  const dataFormatada = data === 'sem-data'
                    ? 'Sem data'
                    : format(new Date(data + 'T00:00:00'), "dd/MM/yyyy (EEEE)", { locale: ptBR });

                  return (
                    <div key={data} className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100">
                      <div>
                        <p className="font-semibold text-sm">{dataFormatada}</p>
                        <p className="text-xs text-muted-foreground">{countData} conta(s)</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">{formatCurrency({ value: totalData, currency: 'BRL' })}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filtros */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar contas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filtroStatus} onValueChange={setFiltroStatus}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="recebido">Recebido</SelectItem>
              <SelectItem value="atrasado">Atrasado</SelectItem>
              <SelectItem value="cancelado">Cancelado</SelectItem>
            </SelectContent>
          </Select>
          <Select value={ordenacao} onValueChange={(value: any) => setOrdenacao(value)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Ordenar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recentes">Mais Recentes</SelectItem>
              <SelectItem value="antigos">Mais Antigos</SelectItem>
              <SelectItem value="cliente-az">Cliente (A-Z)</SelectItem>
              <SelectItem value="cliente-za">Cliente (Z-A)</SelectItem>
              <SelectItem value="valor-maior">Maior Valor</SelectItem>
              <SelectItem value="valor-menor">Menor Valor</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="h-4 w-4 mr-2" />
            Nova Conta
          </Button>
          <Button
            onClick={() => setIsDialogRecebimentosMultiplosOpen(true)}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Múltiplos Recebimentos
          </Button>
          {selectedContasIds.size > 0 && (
            <Button
              onClick={handleAbrirMarcarEmLote}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckSquare className="h-4 w-4 mr-2" />
              Marcar {selectedContasIds.size} Selecionada{selectedContasIds.size > 1 ? 's' : ''}
            </Button>
          )}
        </div>

        {/* Lista de Contas */}
        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <p className="text-muted-foreground mt-2">Carregando contas...</p>
          </div>
        ) : contasFiltradas.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhuma conta encontrada</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {contasFiltradas.filter(c => c.status !== 'recebido').length > 0 && (
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                <Checkbox
                  checked={selectedContasIds.size > 0 && selectedContasIds.size === contasFiltradas.filter(c => c.status !== 'recebido').length}
                  onCheckedChange={toggleSelecionarTodas}
                />
                <label className="text-sm font-medium cursor-pointer">
                  Selecionar Pendentes ({contasFiltradas.filter(c => c.status !== 'recebido').length})
                </label>
              </div>
            )}
            {(() => {
              const totalPages = Math.ceil(contasFiltradas.length / itemsPerPage);
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = startIndex + itemsPerPage;
              const contasPaginadas = contasFiltradas.slice(startIndex, endIndex);

              return (
                <>
                  {contasPaginadas.map((conta) => {
              const cliente = clientes.find(c => c.id === conta.cliente_id);
              const isSelected = selectedContasIds.has(conta.id!);
              return (
                <Card key={conta.id} className={`hover:shadow-md transition-shadow ${isSelected ? 'border-green-500 bg-green-50' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between" onClick={(e) => e.stopPropagation()}>
                      {conta.status !== 'recebido' && (
                        <div className="flex items-center gap-3 mr-4 pt-1">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelecionarConta(conta.id!)}
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold">{conta.descricao}</h3>
                          {getStatusBadge(conta.status)}
                        </div>
                        {cliente && (
                          <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                            <User className="h-4 w-4" />
                            Cliente: {cliente.name}
                          </p>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Valor Total</p>
                            <p className="font-semibold">{formatCurrency({ value: conta.valor_total, currency: 'BRL' })}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Valor Recebido</p>
                            <p className="font-semibold">{formatCurrency({ value: conta.valor_recebido, currency: 'BRL' })}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Vencimento</p>
                            <p className="font-semibold">
                              {format(new Date(conta.data_vencimento), "dd/MM/yyyy", { locale: ptBR })}
                            </p>
                          </div>
                          {conta.data_recebimento && (
                            <div>
                              <p className="text-muted-foreground">Recebimento</p>
                              <p className="font-semibold">
                                {format(new Date(conta.data_recebimento), "dd/MM/yyyy", { locale: ptBR })}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        {conta.status !== 'recebido' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAbrirMarcarRecebido(conta)}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Marcar como Recebido
                          </Button>
                        )}
                        {conta.descricao.includes('PED-') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleVerPedido(conta.descricao)}
                            className="text-purple-600 hover:bg-purple-50 border-purple-200"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Ver Pedido
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenDialog(conta)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(conta.id!)}
                          disabled={isDeleting === conta.id}
                        >
                          {isDeleting === conta.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
                  })}

                  {/* Controles de Paginação */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6 p-4 bg-purple-50 rounded-lg border border-gray-200">
                      <div className="text-sm text-gray-600">
                        Mostrando <span className="font-semibold">{startIndex + 1}</span> a <span className="font-semibold">{Math.min(endIndex, contasFiltradas.length)}</span> de <span className="font-semibold">{contasFiltradas.length}</span> contas
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                          disabled={currentPage === 1}
                          className="border-purple-200 text-purple-600 hover:bg-purple-100"
                        >
                          Anterior
                        </Button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                            const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                            if (page > totalPages) return null;
                            return (
                              <Button
                                key={page}
                                variant={currentPage === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(page)}
                                className={currentPage === page ? "bg-purple-600 text-white" : "border-purple-200 text-purple-600 hover:bg-purple-50"}
                              >
                                {page}
                              </Button>
                            );
                          })}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                          disabled={currentPage === totalPages}
                          className="border-purple-200 text-purple-600 hover:bg-purple-100"
                        >
                          Próxima
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Dialog de criar/editar */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingConta ? "Editar Conta a Receber" : "Nova Conta a Receber"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição *</Label>
                <Input
                  id="descricao"
                  value={formData.descricao || ""}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ClientSearch
                  value={selectedClientName}
                  onChange={handleClientChange}
                  placeholder="Selecione ou crie um cliente"
                />
                <div className="space-y-2">
                  <Label htmlFor="categoria">Categoria</Label>
                  <Input
                    id="categoria"
                    value={formData.categoria || ""}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="valor_total">Valor Total *</Label>
                  <Input
                    id="valor_total"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.valor_total || 0}
                    onChange={(e) => setFormData({ ...formData, valor_total: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="valor_recebido">Valor Recebido</Label>
                  <Input
                    id="valor_recebido"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.valor_recebido || 0}
                    onChange={(e) => setFormData({ ...formData, valor_recebido: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="data_vencimento">Data de Vencimento *</Label>
                  <Input
                    id="data_vencimento"
                    type="date"
                    value={formData.data_vencimento || ""}
                    onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_recebimento">Data de Recebimento</Label>
                  <Input
                    id="data_recebimento"
                    type="date"
                    value={formData.data_recebimento || ""}
                    onChange={(e) => setFormData({ ...formData, data_recebimento: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="forma_pagamento_id">💳 Forma de Pagamento</Label>
                  <Select
                    value={formData.forma_pagamento_id || ""}
                    onValueChange={(value) => setFormData({ ...formData, forma_pagamento_id: value || undefined })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a forma (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {formasPagamento.map((forma) => (
                        <SelectItem key={forma.id} value={forma.id || ""}>
                          {forma.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="conta_bancaria_id">🏦 Conta Bancária</Label>
                  <Select
                    value={formData.conta_bancaria_id || ""}
                    onValueChange={(value) => setFormData({ ...formData, conta_bancaria_id: value || undefined })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a conta (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {contasBancarias.map((conta) => (
                        <SelectItem key={conta.id} value={conta.id || ""}>
                          {conta.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status || "pendente"}
                    onValueChange={(value: any) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendente">Pendente</SelectItem>
                      <SelectItem value="recebido">Recebido</SelectItem>
                      <SelectItem value="atrasado">Atrasado</SelectItem>
                      <SelectItem value="cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="forma_pagamento">Forma (texto livre)</Label>
                  <Input
                    id="forma_pagamento"
                    placeholder="Ex: PIX, Cartão"
                    value={formData.forma_pagamento || ""}
                    onChange={(e) => setFormData({ ...formData, forma_pagamento: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes || ""}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  rows={3}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingConta ? "Atualizando..." : "Criando..."}
                    </>
                  ) : (
                    editingConta ? "Atualizar" : "Criar"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog de Marcar como Recebido */}
        <Dialog open={isMarcarRecebidoDialogOpen} onOpenChange={setIsMarcarRecebidoDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Registrar Recebimento</DialogTitle>
            </DialogHeader>
            {contaParaReceber && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-sm text-muted-foreground">Descrição</p>
                  <p className="font-semibold">{contaParaReceber.descricao}</p>
                  <p className="text-lg font-bold text-blue-600 mt-2">
                    {formatCurrency({ value: contaParaReceber.valor_total, currency: 'BRL' })}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="data_recebimento_dialog">Data de Recebimento</Label>
                  <Input
                    id="data_recebimento_dialog"
                    type="date"
                    value={dataRecebimento}
                    onChange={(e) => setDataRecebimento(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="valor_recebido_dialog">Valor Recebido (R$)</Label>
                  <Input
                    id="valor_recebido_dialog"
                    type="number"
                    step="0.01"
                    min="0"
                    value={valorRecebidoModal}
                    onChange={(e) => setValorRecebidoModal(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="forma_recebimento_dialog">💳 Forma de Pagamento</Label>
                  <Select value={formaRecebimento} onValueChange={setFormaRecebimento}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a forma (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {formasPagamento.map((forma) => (
                        <SelectItem key={forma.id} value={forma.id || ""}>
                          {forma.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formaRecebimento && formasPagamento.find(f => f.id === formaRecebimento)?.taxa_padrao && (
                  <div className="bg-blue-50 border border-blue-200 rounded p-3">
                    <p className="text-xs text-gray-600 mb-1">💳 Desconto de Taxa</p>
                    <div className="text-sm space-y-1">
                      <p>Taxa padrão: <span className="font-semibold">{formasPagamento.find(f => f.id === formaRecebimento)?.taxa_padrao?.toFixed(2)}%</span></p>
                      <p>Valor desconto: <span className="font-semibold text-blue-600">{formatCurrency({
                        value: contaParaReceber.valor_total - (parseFloat(valorRecebidoModal) || 0),
                        currency: 'BRL'
                      })}</span></p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="conta_recebimento_dialog">🏦 Conta Bancária</Label>
                  <Select value={contaRecebimento} onValueChange={setContaRecebimento}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a conta (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {contasBancarias.map((conta) => (
                        <SelectItem key={conta.id} value={conta.id || ""}>
                          {conta.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsMarcarRecebidoDialogOpen(false)}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </Button>
                  <Button onClick={handleConfirmarMarcarRecebido} disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Confirmando...
                      </>
                    ) : (
                      "Confirmar Recebimento"
                    )}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog de Marcar Em Lote */}
        <Dialog open={isMarcarEmLoteDialogOpen} onOpenChange={setIsMarcarEmLoteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Marcar {selectedContasIds.size} Conta(s) como Recebida</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-sm text-muted-foreground">Contas Selecionadas</p>
                <p className="font-semibold text-lg">{selectedContasIds.size}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Valor Total:{' '}
                  {formatCurrency({
                    value: contasFiltradas
                      .filter(c => selectedContasIds.has(c.id!))
                      .reduce((sum, c) => sum + c.valor_total, 0),
                    currency: 'BRL'
                  })}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="data_recebimento_lote">Data de Recebimento</Label>
                <Input
                  id="data_recebimento_lote"
                  type="date"
                  value={dataRecebimentoLote}
                  onChange={(e) => setDataRecebimentoLote(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="forma_recebimento_lote">💳 Forma de Pagamento</Label>
                <Select value={formaRecebimentoLote} onValueChange={setFormaRecebimentoLote}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a forma (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {formasPagamento.map((forma) => (
                      <SelectItem key={forma.id} value={forma.id || ""}>
                        {forma.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="conta_recebimento_lote">🏦 Conta Bancária</Label>
                <Select value={contaRecebimentoLote} onValueChange={setContaRecebimentoLote}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a conta (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {contasBancarias.map((conta) => (
                      <SelectItem key={conta.id} value={conta.id || ""}>
                        {conta.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsMarcarEmLoteDialogOpen(false)}
                  disabled={isProcessandoLote}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmarMarcarEmLote}
                  disabled={isProcessandoLote}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isProcessandoLote ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    `Confirmar ${selectedContasIds.size} Recebimento(s)`
                  )}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog de Múltiplos Recebimentos */}
        <DialogRegistrarRecebimentosMultiplos
          isOpen={isDialogRecebimentosMultiplosOpen}
          onClose={() => setIsDialogRecebimentosMultiplosOpen(false)}
          onRecebimentosCriados={() => {
            queryClient.invalidateQueries({ queryKey: ['contas_receber'] });
            queryClient.invalidateQueries({ queryKey: ['recebiveis_completo'] });
          }}
        />
      </div>
    </div>
  );
}

